import { FC } from "react";
import { PcComponent } from "@app/types";
import {
  IcPartBolt,
  IcPartBoard,
  IcPartBox,
  IcPartChip,
  IcPartDisk,
  IcPartMonitor,
  IcPartRam,
  IcPartVga,
} from "./part-icons";

/**
 * Loại linh kiện theo enum `category` của pc.component.js.
 * Backend ghi "Mainboad" (thiếu r) nên nhận cả 2 cách viết.
 */
export interface PartCategory {
  values: string[];
  name: string;
  sub: string;
  color: string;
  icon: FC<{ className?: string }>;
}

export const PART_CATEGORIES: PartCategory[] = [
  { values: ["Mainboad", "Mainboard"], name: "Mainboard", sub: "Bo mạch chủ", color: "#2563eb", icon: IcPartBoard },
  { values: ["CPU"], name: "CPU", sub: "Bộ vi xử lý", color: "#0891b2", icon: IcPartChip },
  { values: ["VGA"], name: "VGA", sub: "Card đồ hoạ", color: "#16a34a", icon: IcPartVga },
  { values: ["RAM"], name: "RAM", sub: "Bộ nhớ trong", color: "#7c3aed", icon: IcPartRam },
  { values: ["SSD"], name: "SSD", sub: "Ổ cứng SSD", color: "#ea580c", icon: IcPartDisk },
  { values: ["PSU"], name: "PSU", sub: "Nguồn máy", color: "#ca8a04", icon: IcPartBolt },
  { values: ["Combo"], name: "Combo", sub: "Combo linh kiện", color: "#db2777", icon: IcPartBox },
  { values: ["PC Nguyên Case"], name: "PC nguyên case", sub: "Máy đồng bộ", color: "#4f46e5", icon: IcPartMonitor },
];

const FALLBACK: PartCategory = { values: [], name: "", sub: "", color: "#2563eb", icon: IcPartBox };

export const categoryOf = (category?: string) =>
  PART_CATEGORIES.find((c) => c.values.includes(category || "")) || { ...FALLBACK, name: category || "" };

/**
 * Nền ảnh linh kiện theo bản thiết kế (.lk-art): gradient theo màu danh mục, không còn ảnh upload
 */
export const partArtBackground = (color: string) =>
  `radial-gradient(120% 120% at 20% 0%, color-mix(in srgb, ${color} 34%, transparent), transparent 60%), linear-gradient(135deg, color-mix(in srgb, ${color} 16%, var(--surface-2)), var(--surface-2))`;

/** Bóng của ô icon theo màu danh mục (.lk-ico) */
export const partIconShadow = (color: string) => `0 8px 24px color-mix(in srgb, ${color} 30%, transparent)`;

/** Giải mã chuỗi specs thành Object (giữ nguyên như trang cũ) */
export const parseSpecs = (input: any): Record<string, any> => {
  if (!input) {
    return {};
  }

  if (typeof input === "object" && !Array.isArray(input)) {
    return input;
  }

  let parsed: any = input;
  const singleQuote = String.fromCharCode(39);
  const doubleQuote = String.fromCharCode(34);

  while (typeof parsed === "string") {
    const trimmed = parsed.trim();

    if (
      (trimmed.startsWith(doubleQuote) && trimmed.endsWith(doubleQuote)) ||
      (trimmed.startsWith(singleQuote) && trimmed.endsWith(singleQuote))
    ) {
      try {
        parsed = JSON.parse(trimmed);
        continue;
      } catch (e) {
        parsed = trimmed.slice(1, -1);
      }
    }

    const cleanStr = parsed.replace(/\\+/g, "").replace(/\n/g, "").trim();

    try {
      parsed = JSON.parse(cleanStr);
    } catch (e) {
      break;
    }
  }

  const isObject = typeof parsed === "object" && parsed !== null && !Array.isArray(parsed);

  return isObject ? parsed : {};
};

/** Nhãn tiếng Việt cho các key trong specs (như trang cũ) */
export const SPEC_LABELS: Record<string, string> = {
  mainboard: "Mainboard",
  cpu: "CPU",
  socket: "Socket",
  ram: "RAM",
  ram_support: "Hỗ trợ RAM",
  pcie_storage: "PCIe & Lưu trữ",
  performance: "Hiệu năng",
  cores_threads: "Số Nhân / Luồng",
  max_speed: "Xung nhịp tối đa",
  compatibility: "Tương thích",
  compatible: "Tương thích",
  vram: "VRAM",
  target_use: "Nhu cầu",
  capacity: "Dung lượng",
  type: "Loại RAM",
  speed: "Bus RAM",
  brand: "Thương hiệu",
  interface: "Chuẩn cắm",
  read_speed: "Tốc độ đọc",
  write_speed: "Tốc độ ghi",
  power: "Công suất",
  rating: "Hiệu suất",
  form_factor: "Kích thước",
  input_voltage: "Điện áp",
  cpu_support: "CPU Hỗ trợ",
  design: "Thiết kế",
  ssd: "SSD",
  vga: "VGA",
  psu: "Nguồn",
  case: "Vỏ Case",
  storage_support: "Lưu trữ",
};

/** Danh sách [nhãn, giá trị] của specs, bỏ giá trị rỗng */
export const specList = (item: PcComponent): [string, string][] =>
  Object.entries(parseSpecs(item.specs))
    .filter(([, value]) => value)
    .map(([key, value]) => [SPEC_LABELS[key] || key, String(value)]);

/** Các địa điểm của linh kiện: location là chuỗi cách nhau bởi dấu phẩy (như modal cũ) */
export const locationsOf = (item: PcComponent) =>
  String(item.location || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
